// Vietnamese property summary text block, used by the Zalo share button
// and the AI post generation modal.
//
// The output is plain text (not Markdown) because the Zalo chat composer
// pastes it as a single string. We use a couple of leading emojis per
// line as visual anchors — they survive Zalo's text rendering.

import type { Property } from "@/types/property";
import { formatPrice } from "@/features/property/utils/format-price";

export interface PropertySummaryOptions {
  agentName?: string | undefined;
  agentPhone?: string | undefined;
}

/**
 * Build a Vietnamese property summary block. Lines render in this order:
 *
 *   🏠 [Bán/Cho thuê] [Building name]
 *   📍 [Ward display name]
 *   📐 [Area] m² · [Bedrooms] PN · [Bathrooms] WC
 *   💰 [Formatted price]
 *   📞 [Agent name + phone]    ← only if name OR phone given
 *
 * Empty values are skipped so an entry like "0 PN" never appears.
 */
export function formatPropertySummary(
  property: Property,
  options: PropertySummaryOptions = {},
): string {
  const lines: string[] = [];

  const listingLabel = property.listingType === "SALE" ? "Bán" : "Cho thuê";
  const titleParts: string[] = [listingLabel];
  if (property.buildingName && property.buildingName.trim().length > 0) {
    titleParts.push(property.buildingName.trim());
  }
  lines.push(`🏠 ${titleParts.join(" ")}`);

  if (property.wardDisplayName !== "") {
    lines.push(`📍 ${property.wardDisplayName}`);
  }

  const specs: string[] = [];
  if (Number.isFinite(property.area) && property.area > 0) {
    specs.push(`${property.area} m²`);
  }
  if (property.bedrooms !== null && property.bedrooms > 0) {
    specs.push(`${property.bedrooms} PN`);
  }
  if (property.bathrooms !== null && property.bathrooms > 0) {
    specs.push(`${property.bathrooms} WC`);
  }
  if (specs.length > 0) {
    lines.push(`📐 ${specs.join(" · ")}`);
  }

  lines.push(`💰 ${formatPrice(property.price, property.listingType)}`);

  const contactParts: string[] = [];
  if (options.agentName !== undefined && options.agentName.trim().length > 0) {
    contactParts.push(options.agentName.trim());
  }
  if (options.agentPhone !== undefined && options.agentPhone.trim().length > 0) {
    contactParts.push(options.agentPhone.trim());
  }
  if (contactParts.length > 0) {
    lines.push(`📞 ${contactParts.join(" · ")}`);
  }

  return lines.join("\n");
}
